import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  getTeams,
  getTeamsByCompetition,
} from '../../../services/util/gamesAndTeams/getTeams';
import CustomButton from '../../shared/button/CustomButton';
import { StyledBackdrop, StyledSearchBar } from './Search.styled';
import { useGames } from '../../shared/hooks/useGames';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { StyledSelectedTeams } from '../selectedOverview/SelectedFavourites.styled';
import useCompetitionsFromSelectedTeams from '../../shared/hooks/useCompetitionsFromSelectedTeams';
import mapSearchResult from './mapSearchResult';
import { MAX_AMOUNT, SHOWN_COMPETITIONS } from './Popular.constant';
import ErrorSnackbar from '../../shared/snackbar/ErrorSnackbar';
import useSelectedFavourites from '../../../zustand/SelectedFavourites';
import OptionsFilterModal from './OptionsFilterModal';
import useHandleClickFavourites from '../../shared/hooks/useHandleClickFavourites';

const SearchBar: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [lastSearches, setLastSearches] = useState<string[]>([]);
  const [displayedCompetitions, setDisplayedCompetitions] = useState(
    SHOWN_COMPETITIONS.slice(0, 3)
  );
  const { handleRemoveTeam, handleRemoveCompetition, handleAddCompetition } =
    useHandleClickFavourites();
  const { games: allGames, loading, error } = useGames();
  const {
    selectedTeams: selectedTeamsGlobal,
    selectedCompetitions: selectedCompetitionsGlobal,
    setSelectedTeams: setSelectedTeamsGlobal,
  } = useSelectedFavourites();
  const teams: string[] = useMemo(() => {
    if (loading || error) {
      return [];
    }
    return getTeams(allGames);
  }, [allGames, loading, error]);

  const allCompetitions = useCompetitionsFromSelectedTeams({
    teams,
    allGames,
  });

  React.useEffect(() => {
    const storedLastSearches = localStorage.getItem('lastSearches');
    if (storedLastSearches) {
      setLastSearches(JSON.parse(storedLastSearches));
    }
  }, []);

  React.useEffect(() => {
    if (lastSearches.length > 0) {
      localStorage.setItem('lastSearches', JSON.stringify(lastSearches));
    }
  }, [lastSearches]);

  const filteredTeams = useMemo(() => {
    return teams.filter((team) =>
      team.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, teams]);

  const filteredCompetitions = useMemo(() => {
    return allCompetitions.filter((competition) =>
      competition.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, allCompetitions]);

  function selectAllTeams() {
    setSelectedTeamsGlobal(teams);
  }
  function handleOpen() {
    setIsModalOpen(true);
  }
  function handleClose() {
    setIsModalOpen(false);
  }
  function handleAddTeam(team: string) {
    if (!selectedTeamsGlobal.includes(team)) {
      setSelectedTeamsGlobal([...selectedTeamsGlobal, team]);
    }
    setLastSearches((prev) => {
      const updatedSearches = [team, ...prev.filter((item) => item !== team)];
      if (updatedSearches.length > 5) {
        updatedSearches.pop();
      }
      return updatedSearches;
    });
  }

  const handleShowMore = () => {
    if (displayedCompetitions.length > 3) {
      setDisplayedCompetitions(SHOWN_COMPETITIONS.slice(0, 3));
    } else {
      setDisplayedCompetitions(SHOWN_COMPETITIONS);
    }
  };
  return (
    <>
      <StyledSearchBar>
        <TextField
          type="search"
          label="Search for teams or competitions"
          variant="filled"
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          maxRows={1}
        />
        <CustomButton
          variant="text"
          size="small"
          onClick={handleOpen}
          label="Options"
        />
      </StyledSearchBar>
      <StyledSelectedTeams>
        <Paper sx={{ padding: 3 }}>
          {searchQuery !== '' ? (
            <>
              {mapSearchResult({
                caption: 'Teams',
                filteredData: filteredTeams,
                MAX_AMOUNT: MAX_AMOUNT,
                handleAdd: handleAddTeam,
                handleRemove: handleRemoveTeam,
                selectedData: selectedTeamsGlobal,
                isScrollableDisplayed: false,
              })}

              {mapSearchResult({
                caption: 'Competitions',
                filteredData: filteredCompetitions,
                MAX_AMOUNT: MAX_AMOUNT,
                handleAdd: handleAddCompetition,
                handleRemove: handleRemoveCompetition,
                selectedData: selectedCompetitionsGlobal,
                toolTipText:
                  'Selecting a competition means you select all games of the competition',
                isScrollableDisplayed: true,
              })}
            </>
          ) : (
            <>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                fontStyle="oblique"
              >
                Recommendations
              </Typography>
              {lastSearches.length > 0 && (
                <>
                  {mapSearchResult({
                    caption: 'Past selected teams',
                    filteredData: lastSearches,
                    MAX_AMOUNT: MAX_AMOUNT,
                    handleAdd: handleAddTeam,
                    handleRemove: handleRemoveTeam,
                    selectedData: selectedTeamsGlobal,
                    isScrollableDisplayed: true,
                  })}
                </>
              )}
              {displayedCompetitions.map((competition) => (
                <React.Fragment key={competition.name}>
                  {mapSearchResult({
                    caption: (
                      <>
                        <img
                          src={competition.logo}
                          width={'30px'}
                          style={{ borderRadius: '8px', marginRight: '7px' }}
                          alt="CompetitionLogo"
                        />{' '}
                        {competition.name}
                      </>
                    ),
                    filteredData: getTeamsByCompetition(
                      allGames,
                      competition.name
                    ),
                    MAX_AMOUNT: MAX_AMOUNT,
                    handleAdd: handleAddTeam,
                    handleRemove: handleRemoveTeam,
                    selectedData: selectedTeamsGlobal,
                    isScrollableDisplayed: true,
                  })}
                </React.Fragment>
              ))}
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginTop={2}
              >
                <Chip
                  label={
                    displayedCompetitions.length > 3 ? 'Show less' : 'Show more'
                  }
                  onClick={handleShowMore}
                  variant="filled"
                  color="secondary"
                />
              </Box>
            </>
          )}
        </Paper>
      </StyledSelectedTeams>

      {loading && (
        <StyledBackdrop open={loading}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      {error && <ErrorSnackbar open customError="Error fetching games data" />}
      {isModalOpen && (
        <OptionsFilterModal
          handleClose={handleClose}
          selectAllTeams={selectAllTeams}
        />
      )}
    </>
  );
};

export default SearchBar;
